import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AtSign, BellOff, BellRing, CheckCheck, Inbox as InboxIcon, Mail, Settings2, UserPlus } from 'lucide-react';
import { Button } from '@missions/ui';
import { useApi } from '../lib/hooks';
import { pushPermission } from '../lib/push';
import { setPageTitle } from '../lib/seo';
import { useChrome, useShell } from '../layout/shell-context';
import { GoToCard } from '../layout/sidebar-parts';
import { NotificationFeed, NotifTabs, type NotifTab, useMarkAllRead } from '../features/notifications/NotificationList';
import { PushCard } from '../features/notifications/NotificationsPanel';

/** Inbox page: same feed as the bell panel, with tabs in the secondary sidebar (mockups/app.html #pgInbox). */
export function InboxPage() {
  const [tab, setTab] = useState<NotifTab>('all');
  const counts = useApi('notifications.counts');
  const markAll = useMarkAllRead();
  const shell = useShell();
  const nav = useNavigate();
  useEffect(() => setPageTitle('Inbox'), []);
  const c = counts.data;
  const push = pushPermission();
  useChrome(
    {
      section: 'Work',
      current: <><InboxIcon style={{ color: 'var(--text-3)' }} /><span className="nm">Inbox</span></>,
      secondaryTitle: 'Inbox',
      hideFab: true,
      secondary: (
        <>
          <div className="glabel" style={{ paddingTop: 4 }}>Inbox</div>
          {(
            [
              ['all', 'All', <InboxIcon key="a" />, 0],
              ['unread', 'Unread', <Mail key="u" />, c?.unread ?? 0],
              ['mentions', 'Mentions', <AtSign key="m" />, c?.mentions ?? 0],
              ['assigned', 'Assigned to me', <UserPlus key="s" />, c?.assigned ?? 0],
            ] as const
          ).map(([k, l, i, n]) => (
            <button key={k} className={`si${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>
              {i}
              <span className="lbl">{l}</span>
              {n ? <span className="cnt">{n}</span> : null}
            </button>
          ))}
          <div className="glabel">Settings</div>
          <button className="si" onClick={() => nav('/settings/notifications')}><Settings2 /><span className="lbl">Preferences</span></button>
          <button className="si" onClick={() => nav('/settings/notifications')}>
            {push === 'granted' ? <BellRing /> : <BellOff />}
            <span className="lbl">Push: {push === 'granted' ? 'On' : push === 'denied' ? 'Blocked' : 'Off'}</span>
          </button>
        </>
      ),
      rail: (
        <>
          <div className="rcard">
            <h4>How notifications work</h4>
            <div className="legend" style={{ flexDirection: 'column', gap: 10, fontSize: '0.8125rem', color: 'var(--text-2)' }}>
              <span><i className="lg unseen" /><b>New</b>: not on screen yet, counted in the bell badge</span>
              <span><i className="lg seen" /><b>Seen</b>: on screen for 1 second, still unread</span>
              <span><i className="lg read" /><b>Read</b>: you opened it, or marked it as read</span>
            </div>
          </div>
          <GoToCard />
        </>
      ),
    },
    [tab, c, push],
  );
  return (
    <div className="page inbox" id="pgInbox">
      <div className="inbox-h">
        <div>
          <h1>Inbox</h1>
          <p>Updates about missions you’re assigned to, mentioned in or following.</p>
        </div>
        <span className="sp" />
        <Button onClick={() => void markAll()}><CheckCheck />Mark all as read</Button>
        <Button variant="ghost" onClick={() => shell.openPrefs()} aria-label="Notification preferences"><Settings2 /><span className="hide-m">Preferences</span></Button>
      </div>
      <div className="card">
        <PushCard />
        <div style={{ paddingTop: 12 }}><NotifTabs tab={tab} onTab={setTab} /></div>
        <NotificationFeed tab={tab} />
      </div>
    </div>
  );
}
